import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import { cn } from 'src/utils/cn';

type Props = {
  lines: number;
  children: ReactNode;
};

export const ExpandableText: FC<Props> = ({ children, lines }) => {
  const [expanded, setExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || expanded) {
      return;
    }
    // to detect whether line clamping had an effect and we are hiding things, we compare scrollHeight and clientHeight
    // this check only works in expanded=false state, because otherwise both are same
    const clientHeight = containerRef.current.clientHeight;
    const scrollHeight = containerRef.current.scrollHeight;
    if (clientHeight >= scrollHeight) {
      // the rendered text is smaller than the cutoff height for the ellipsis, so we don't need any show more/less
      setNeedsExpansion(false);
      setExpanded(true);
    } else {
      // we are bigger than cutoff height, so cut off and add show more button
      setNeedsExpansion(true);
      setExpanded(false);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [children, containerRef]);

  return (
    <div className={cn(`flex flex-col gap-xs`)}>
      <div
        ref={containerRef}
        className={cn(
          !expanded && `truncate overflow-hidden whitespace-normal`
        )}
        style={
          !expanded
            ? {
                WebkitLineClamp: `${lines}`,
                WebkitBoxOrient: 'vertical',
                display: '-webkit-box',
              }
            : undefined
        }
      >
        {children}
      </div>

      {needsExpansion && (
        <div className={cn(`flex justify-end`)}>
          <Button mode="link-underlined" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'show less' : 'show more'}
          </Button>
        </div>
      )}
    </div>
  );
};
